import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, FileText, AlertCircle, Shield, Heart, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';

const ParentConsentPage = () => {
  const { colors } = useTheme();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [consentData, setConsentData] = useState(null);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [signatureName, setSignatureName] = useState('');
  const [mediaReleaseConsent, setMediaReleaseConsent] = useState(true);
  const [agreementsRead, setAgreementsRead] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  
  const contentRef = useRef(null);

  useEffect(() => {
    if (token) {
      fetchConsentData();
    } else {
      setError('Invalid consent link. Please use the link from your email.');
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchConsentData = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/agreements/parent-consent/${token}`
      );
      
      if (res.ok) {
        const data = await res.json();
        setConsentData(data);
        setSignatureName(data.parent_name || '');
      } else {
        const errorData = await res.json();
        setError(errorData.detail || 'This consent link is invalid or has expired.');
      }
    } catch (err) {
      setError('Failed to load consent form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAgreementScroll = (e, agreementId) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight * 0.9) {
      setAgreementsRead(prev => ({ ...prev, [agreementId]: true }));
    }
  };

  const handleSubmit = async () => {
    if (!signatureName.trim()) {
      toast.error('Please enter your signature');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/agreements/parent-consent/${token}/sign?signature_name=${encodeURIComponent(signatureName)}&media_release_consent=${mediaReleaseConsent}`,
        { method: 'POST' }
      );

      if (res.ok) {
        setCompleted(true);
        toast.success('Consent form signed successfully!');
      } else {
        const errorData = await res.json();
        toast.error(errorData.detail || 'Failed to submit consent form');
      }
    } catch (err) {
      toast.error('Failed to submit consent form');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full" style={{ borderColor: colors.primary }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: colors.background }}>
        <div className="max-w-md w-full rounded-2xl p-8 text-center shadow-lg" style={{ backgroundColor: colors.surface }}>
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold mb-2" style={{ color: colors.text }}>Unable to Load Form</h1>
          <p style={{ color: colors.text_muted }}>{error}</p>
          <p className="text-sm mt-4" style={{ color: colors.text_muted }}>
            If you need assistance, contact us at{' '}
            <a href="mailto:support@YASConnect.co" className="hover:underline" style={{ color: colors.primary }}>
              support@YASConnect.co
            </a>
          </p>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: colors.background }}>
        <div className="max-w-md w-full rounded-2xl p-8 text-center shadow-lg" style={{ backgroundColor: colors.surface }}>
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: colors.text }}>Thank You!</h1>
          <p className="mb-4" style={{ color: colors.text_muted }}>
            Your consent has been successfully recorded. <strong>{consentData?.youth_name}</strong> now has full access to YAS Connect!
          </p>
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-left">
            <h3 className="font-semibold text-green-800 mb-2">What's Next?</h3>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• Your child can now fully participate in the program</li>
              <li>• You can create a parent account to monitor their progress</li>
              <li>• You'll receive updates on their achievements</li>
            </ul>
          </div>
          <p className="text-sm mt-6" style={{ color: colors.text_muted }}>
            You can close this window now.
          </p>
        </div>
      </div>
    );
  }

  const agreements = consentData?.agreements || [];
  const currentAgreement = agreements[currentStep];
  const isLastStep = currentStep === agreements.length;
  const canProceed = currentStep < agreements.length ? agreementsRead[currentAgreement?.id] : true;

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="text-white py-8 px-4" style={{ background: colors.header_gradient || colors.header }}>
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-6 h-6" />
            <span className="text-sm font-semibold opacity-90">Parent/Guardian Consent</span>
          </div>
          <h1 className="text-2xl font-bold">YAS Connect</h1>
          <p className="text-white/80 mt-2">
            Consent form for <strong>{consentData?.youth_name}</strong>
          </p>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white border-b">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {agreements.map((_, idx) => (
              <React.Fragment key={idx}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  idx < currentStep ? 'bg-green-500 text-white' :
                  idx === currentStep ? 'bg-primary text-white' :
                  'bg-gray-200 text-gray-500'
                }`}>
                  {idx < currentStep ? <Check className="w-4 h-4" /> : idx + 1}
                </div>
                {idx < agreements.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${
                    idx < currentStep ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              currentStep === agreements.length ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              <Check className="w-4 h-4" />
            </div>
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            {agreements.map((a, idx) => (
              <span key={idx} className={idx === currentStep ? 'text-primary font-semibold' : ''}>
                {a.title.replace('YAS CONNECT ', '').replace('Privacy Policy', 'Privacy').substring(0, 15)}
              </span>
            ))}
            <span className={currentStep === agreements.length ? 'text-primary font-semibold' : ''}>
              Sign
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {currentStep < agreements.length ? (
          // Agreement viewing step
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-primary" />
                <div>
                  <h2 className="font-bold text-gray-900">{currentAgreement?.title}</h2>
                  <p className="text-sm text-gray-500">
                    Please read carefully and scroll to the bottom
                  </p>
                </div>
              </div>
            </div>
            
            <div 
              ref={contentRef}
              onScroll={(e) => handleAgreementScroll(e, currentAgreement?.id)}
              className="p-6 overflow-y-auto"
              style={{ maxHeight: '50vh', backgroundColor: colors.card }}
            >
              <div 
                className="prose prose-sm max-w-none"
                style={{ color: colors.text }}
                dangerouslySetInnerHTML={{ __html: currentAgreement?.content }}
              />
            </div>

            <div className="p-4 border-t" style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
              {!agreementsRead[currentAgreement?.id] && (
                <p className="text-sm mb-3 flex items-center gap-2" style={{ color: '#D97706' }}>
                  <AlertCircle className="w-4 h-4" />
                  Please scroll to read the entire document
                </p>
              )}
              <div className="flex gap-3">
                {currentStep > 0 && (
                  <Button variant="outline" onClick={() => setCurrentStep(prev => prev - 1)}>
                    Back
                  </Button>
                )}
                <Button 
                  className="flex-1"
                  onClick={() => setCurrentStep(prev => prev + 1)}
                  disabled={!agreementsRead[currentAgreement?.id]}
                >
                  I've Read This - Continue
                </Button>
              </div>
            </div>
          </div>
        ) : (
          // Signature step
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Almost Done!</h2>
              <p className="text-gray-600 mt-2">
                Please sign below to give consent for <strong>{consentData?.youth_name}</strong> to use YAS Connect.
              </p>
            </div>

            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3">You're agreeing to:</h3>
                <ul className="space-y-2">
                  {agreements.map((a, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-green-500" />
                      {a.title}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Media Release Option */}
              <div className="border rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="mediaRelease"
                    checked={mediaReleaseConsent}
                    onCheckedChange={setMediaReleaseConsent}
                  />
                  <div>
                    <Label htmlFor="mediaRelease" className="font-semibold cursor-pointer">
                      Media Release Consent (Optional)
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">
                      I grant permission for EBEC to use photos, videos, and achievements of my child for program documentation and promotional materials.
                    </p>
                  </div>
                </div>
              </div>

              {/* Signature */}
              <div>
                <Label htmlFor="signature" className="font-semibold" style={{ color: '#111827', fontSize: '18px' }}>
                  Your Full Legal Name (Electronic Signature)
                </Label>
                <Input
                  id="signature"
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  placeholder="Type your full name to sign"
                  className="mt-2"
                  style={{
                    fontSize: '24px',
                    padding: '16px',
                    backgroundColor: '#FFFFFF',
                    color: '#000000',
                    border: '2px solid #3B82F6',
                    borderRadius: '8px',
                    fontFamily: 'cursive',
                    textAlign: 'center',
                    fontWeight: '600'
                  }}
                />
                <p className="text-xs mt-2" style={{ color: '#4B5563', fontWeight: '600' }}>
                  By typing your name, you acknowledge that this is a legally binding electronic signature.
                </p>
              </div>

              {/* Submit */}
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setCurrentStep(prev => prev - 1)}
                >
                  Back
                </Button>
                <Button 
                  className="flex-1"
                  onClick={handleSubmit}
                  disabled={!signatureName.trim() || isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Sign & Submit Consent'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="max-w-2xl mx-auto px-4 py-6 text-center">
        <p className="text-sm text-gray-500">
          Questions? Contact us at{' '}
          <a href="mailto:support@YASConnect.co" className="text-primary hover:underline">
            support@YASConnect.co
          </a>
        </p>
      </div>
    </div>
  );
};

export default ParentConsentPage;
